import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import { M } from '../servicio/ServicioUI';

/*
 * Corregir el cobro de un trabajo ya hecho (3-nov-2026): para los que quedaron mal cargados (sin PDF, en otro día,
 * sin repuestos, cobrados de otra forma). Fecha, cuánto se cobró, cómo, cuánto fueron los repuestos y la parte del
 * técnico. La cuenta de abajo la hace el servidor con las mismas reglas que la liquidación (simular = no guarda).
 */
const input = 'w-full mt-1 h-12 px-3 rounded-xl text-body font-bold outline-none border bg-chip border-black/[0.07] dark:border-white/[0.07] text-ink focus:ring-2 focus:ring-[#D13A28]/20';
const num = (v) => Number(String(v ?? '').replace(/\./g, '').replace(',', '.')) || 0;

function Opcion({ activa, onClick, children }) {
    return (
        <button type="button" onClick={onClick}
            className={`flex-1 h-11 rounded-xl text-label font-black active:scale-95 border ${activa ? 'bg-[#C9341F] text-white border-transparent' : 'bg-chip text-secondary border-black/10 dark:border-white/10'}`}>
            {children}
        </button>
    );
}

export default function CorregirCobroSheet({ servicio: s, total, onCerrar, onGuardado }) {
    const [fecha, setFecha] = useState(String(s.fecha || '').slice(0, 10));
    const [monto, setMonto] = useState(String(Math.round(Number(s.montoFinal) > 0 ? Number(s.montoFinal) : Number(total) || 0)));
    const [modalidad, setModalidad] = useState(s.modalidadCobro === 'EFECTIVO_SIN_FACTURA' ? 'EFECTIVO_SIN_FACTURA' : 'CON_FACTURA');
    const [repuestos, setRepuestos] = useState(null);       // se llena con lo que ya tiene cargado
    const [pct, setPct] = useState(s.porcentajeTecnico ?? 50);
    // Qué repuesto fue: escondido, solo si hace falta que salga el nombre en el PDF
    const [verNombre, setVerNombre] = useState(false);
    const [nombreRep, setNombreRep] = useState('');
    const [cobrado, setCobrado] = useState(true);
    const [cuenta, setCuenta] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const pedido = useRef(0);

    const body = (extra = {}) => ({
        fecha, montoFinal: num(monto), modalidadCobro: modalidad,
        ...(repuestos === null ? {} : { repuestos: num(repuestos) }),
        ...(nombreRep.trim() ? { repuestosNombre: nombreRep.trim() } : {}),
        porcentajeTecnico: pct, cobrado, ...extra,
    });

    // La cuenta en vivo (con una pausa corta para no pedirla en cada tecla)
    useEffect(() => {
        if (num(monto) <= 0) { setCuenta(null); return undefined; }
        const n = ++pedido.current;
        const t = setTimeout(() => {
            api.patch(`/servicios/${s.id}/corregir-cobro`, body({ simular: true }))
                .then(r => {
                    if (n !== pedido.current) return;
                    setCuenta(r.data.reparto);
                    if (repuestos === null) setRepuestos(String(Math.round(Number(r.data.reparto?.repuestos) || 0)));
                })
                .catch(() => { if (n === pedido.current) setCuenta(null); });
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fecha, monto, modalidad, repuestos, pct, cobrado, s.id]);

    const guardar = async () => {
        if (num(monto) <= 0) { toast.error('Poné cuánto se cobró'); return; }
        setGuardando(true);
        try {
            const r = await api.patch(`/servicios/${s.id}/corregir-cobro`, body());
            toast.success('Cobro corregido');
            onGuardado?.(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.message || 'No se pudo guardar');
        } finally {
            setGuardando(false);
        }
    };

    const esDueno = cuenta?.esDueno;
    const linea = 'flex justify-between items-center py-2 text-body';

    return (
        <ModalShell titulo="Corregir cobro" subtitulo={[s.clienteNombre, `#${s.id}`].filter(Boolean).join(' · ')} onCerrar={onCerrar} lateral
            pie={
                <div className="flex gap-2">
                    <button type="button" onClick={onCerrar} className="flex-1 h-12 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">Cancelar</button>
                    <button type="button" onClick={guardar} disabled={guardando}
                        className="flex-[2] h-12 rounded-xl bg-[#C9341F] text-white text-body font-black active:scale-95 disabled:opacity-50">
                        {guardando ? 'Guardando…' : 'Guardar'}
                    </button>
                </div>
            }>
            <div className="space-y-4">
                <label className="block">
                    <span className="text-caption font-bold text-muted">Fecha del trabajo</span>
                    <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className={input} />
                </label>
                <label className="block">
                    <span className="text-caption font-bold text-muted">Cuánto se cobró en total</span>
                    <input type="text" inputMode="numeric" value={monto} onChange={e => setMonto(e.target.value)} className={input} placeholder="$" />
                </label>
                <div>
                    <span className="text-caption font-bold text-muted">Cómo pagó</span>
                    <div className="flex gap-2 mt-1">
                        <Opcion activa={modalidad === 'EFECTIVO_SIN_FACTURA'} onClick={() => setModalidad('EFECTIVO_SIN_FACTURA')}>Efectivo</Opcion>
                        <Opcion activa={modalidad === 'CON_FACTURA'} onClick={() => setModalidad('CON_FACTURA')}>Transferencia</Opcion>
                    </div>
                </div>
                <label className="block">
                    <span className="text-caption font-bold text-muted">De eso, cuánto fueron repuestos</span>
                    <input type="text" inputMode="numeric" value={repuestos ?? ''} onChange={e => setRepuestos(e.target.value)} className={input} placeholder="$0" />
                    <span className="block mt-1 text-caption text-muted">A precio de venta. Va para DLT antes de repartir.</span>
                </label>
                {num(repuestos) > 0 && (verNombre ? (
                    <label className="block -mt-2">
                        <span className="text-caption font-bold text-muted">Qué repuesto fue (sale en el PDF)</span>
                        <input type="text" value={nombreRep} onChange={e => setNombreRep(e.target.value)} className={input}
                            placeholder="Ej: Manguera interna" autoFocus />
                    </label>
                ) : (
                    <button type="button" onClick={() => setVerNombre(true)} className="-mt-2 text-caption font-bold text-muted underline underline-offset-2">
                        Poner qué repuesto fue
                    </button>
                ))}
                {!esDueno && (
                    <div>
                        <span className="text-caption font-bold text-muted">Parte del técnico de la mano de obra</span>
                        <div className="flex gap-2 mt-1">
                            <Opcion activa={pct === 50} onClick={() => setPct(50)}>Mitad (50%)</Opcion>
                            <Opcion activa={pct === 100} onClick={() => setPct(100)}>Todo (100%)</Opcion>
                        </div>
                    </div>
                )}
                {s.estado !== 'COBRADO' && (
                    <label className="flex items-center gap-2 text-body font-bold text-ink">
                        <input type="checkbox" checked={cobrado} onChange={e => setCobrado(e.target.checked)} className="w-5 h-5 accent-[#C9341F]" />
                        {s.estado === 'ARCHIVADO' ? 'Ya está cobrado (sale de Archivados)' : 'Ya está cobrado'}
                    </label>
                )}

                {cuenta && (
                    <div className="rounded-2xl bg-panel px-4 py-2 divide-y divide-black/5 dark:divide-white/5">
                        <div className={linea}><span className="text-muted">Cobrado</span><M valor={num(monto)} className="font-bold text-ink" /></div>
                        <div className={linea}><span className="text-muted">− Repuestos</span><M valor={Number(cuenta.repuestos)} className="font-bold text-ink" /></div>
                        {Number(cuenta.impuestosReparto) > 0 && (
                            <div className={linea}><span className="text-muted">− Impuestos</span><M valor={Number(cuenta.impuestosReparto)} className="font-bold text-ink" /></div>
                        )}
                        <div className={linea}><span className="text-muted">Mano de obra a repartir</span><M valor={Number(cuenta.netoTecnico)} className="font-bold text-ink" /></div>
                        {!esDueno && (
                            <div className={linea}><span className="font-black text-ink">Le toca a {s.usuarioNombre?.split(' ')[0] || 'el técnico'}</span>
                                <M valor={Number(cuenta.parteTecnico)} className="text-body-lg font-black text-[#16A34A]" /></div>
                        )}
                        <div className={linea}><span className="font-black text-ink">Le queda a DLT</span>
                            <M valor={Number(cuenta.teDeja)} className="text-body-lg font-black text-ink" /></div>
                    </div>
                )}
            </div>
        </ModalShell>
    );
}
